import { type FC, useMemo } from 'react'

import { useAutomatedMessageContext } from '@liveHelper/components/AutomatedMessageProvider'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { DEFAULT_AGENT_THUMBNAIL } from '@liveHelper/constants'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useCloseWidgetContext } from '@liveHelper/context/CloseWidgetContext'
import {
  actionsSelector,
  conversationSelector,
  useStore,
} from '@liveHelper/models/store'
import { LIVE_HELPER_WIDGET_BASE_CONTAINER } from '@liveHelper/testIds'
import { useTrackClickWidget } from '@liveHelper/tracking/useLiveHelperTracking'

import { Close } from '@core/assets/Close'
import { useLocalStorage } from '@core/hooks/useLocalStorage'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'

import {
  AgentImage,
  AgentImageContainer,
  AgentImageFallback,
  CloseWidgetButton,
  FoldedText,
  FoldedTextWrapper,
  IdleFoldedAnimatedContainer,
} from './Idle.styles'

/**
 * Folded animated state of the idle widget.
 */
export const IdleFoldedAnimated: FC = () => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()
  const chatChannelConfig = useChatChannelConfig()

  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const conversation = useStore(conversationSelector)

  const [lastEngagedConversation] = useLocalStorage('lastEngagedConversation')
  const { lastUnreadMessage } = useMessagePreviewContext()
  const { automatedMessage } = useAutomatedMessageContext()
  const { canCloseWidget, closeWidget } = useCloseWidgetContext()
  const trackClickWidget = useTrackClickWidget()

  const conversationStarted = !!(
    conversation && lastEngagedConversation === conversation?.id
  )

  const agentAvatarUrl = useMemo(
    function (): string {
      // Show the avatar of the agent that sent the last unread message
      // There is a specific UI for this case, thus its the highest priority
      if (lastUnreadMessage?.avatar) {
        return lastUnreadMessage.avatar
      }
      if (
        automatedMessage &&
        chatChannelConfig?.widget_style?.agent_avatars?.length
      ) {
        const url =
          chatChannelConfig.widget_style.agent_avatars[
            chatChannelConfig.widget_style.agent_avatars.length - 1
          ]?.url
        if (url) {
          return url
        }
      }
      return (
        chatChannelConfig?.widget_style?.thumbnail_url ||
        DEFAULT_AGENT_THUMBNAIL
      )
    },
    [lastUnreadMessage, automatedMessage, chatChannelConfig],
  )

  const handleCloseWidget = (e) => {
    e.stopPropagation()
    closeWidget()
  }

  return (
    <IdleFoldedAnimatedContainer
      aria-label={t('oneToOne:One-to-one chat')}
      onClick={() => {
        conversation
          ? sendUIStateTransitionEvent('START_CHAT')
          : sendUIStateTransitionEvent('EXPAND')
        trackClickWidget({
          click_target: 'minimized_widget',
          last_unread_message: lastUnreadMessage?.message,
        })
      }}
      hasLastUnreadMessage={!!lastUnreadMessage || !!automatedMessage}
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
    >
      {canCloseWidget && (
        <CloseWidgetButton
          aria-label={t(`oneToOne:Close widget`)}
          actionType="neutral"
          buttonSize="xs"
          shape="circle"
          onClick={handleCloseWidget}
          icon={<Close height="8px" width="8px" label="" aria-hidden />}
        />
      )}
      <AgentImageContainer
        hasLastUnreadMessage={!!lastUnreadMessage || !!automatedMessage}
      >
        <AgentImage
          src={agentAvatarUrl}
          className="chromatic-ignore"
          alt={t(`oneToOne:Agent picture`)}
          fallbackComponent={<AgentImageFallback />}
        />
        {!lastUnreadMessage && !automatedMessage ? (
          <FoldedTextWrapper>
            <FoldedText className={tw?.('text-sm')}>
              {conversationStarted
                ? t(`oneToOne:Return to chat`)
                : chatChannelConfig?.widget_style?.idle_state_text ||
                  t(`oneToOne:Ask me anything`)}
            </FoldedText>
          </FoldedTextWrapper>
        ) : null}
      </AgentImageContainer>
    </IdleFoldedAnimatedContainer>
  )
}
